import { z } from 'zod';
import { paginationQuerySchema } from './pagination.js';
/**
 * Push notifications (D96-D99). Sent through the Expo push service, which
 * hands Android messages to Firebase Cloud Messaging and iOS ones to APNs.
 * Every message is also stored in the agent's in-app list, so nothing is lost
 * when a push is missed.
 */
export const PUSH_PLATFORMS = ['android', 'ios'];
/** Which push service holds the token: the Expo service (the old RN app) or FCM v1 directly (the Flutter app). */
export const PUSH_PROVIDERS = ['expo', 'fcm'];
const EXPO_TOKEN_PATTERN = /^(ExponentPushToken|ExpoPushToken)\[[^\]]+\]$/;
/**
 * The two token shapes are kept disjoint on purpose: the server routes a push
 * by its token, so an Expo-shaped token may not register as 'fcm'. Old app
 * builds send no `provider`; they are Expo by definition.
 */
export const registerPushTokenRequestSchema = z.union([
    z.object({
        /** ExponentPushToken[...] from expo-notifications. */
        token: z.string().min(10).max(300).regex(EXPO_TOKEN_PATTERN, 'Not an Expo push token'),
        platform: z.enum(PUSH_PLATFORMS),
        provider: z.literal('expo').default('expo'),
    }),
    z.object({
        /** An opaque FCM registration token from firebase_messaging. */
        token: z
            .string()
            .min(20)
            .max(512)
            .refine((t) => !EXPO_TOKEN_PATTERN.test(t), 'An Expo token must register as provider "expo"'),
        platform: z.enum(PUSH_PLATFORMS),
        provider: z.literal('fcm'),
    }),
]);
/** TEST: an admin's "send a test notification to myself" (D104). */
export const NOTIFICATION_KINDS = ['ASSIGNMENT', 'ANNOUNCEMENT', 'TEST'];
/** Assignment notifications are essential and cannot be muted (D99); neither can a test, or it would prove nothing. */
export const ESSENTIAL_KINDS = ['ASSIGNMENT', 'TEST'];
export const isEssential = (kind) => ESSENTIAL_KINDS.includes(kind);
/** Screens a notification may open in the app. Anything else is refused (no free-form URLs). */
export const DEEP_LINK_TARGETS = ['home', 'tasks', 'pending', 'performance', 'settings', 'notifications', 'assignment'];
export const deepLinkSchema = z
    .object({ target: z.enum(DEEP_LINK_TARGETS), id: z.uuid().nullable().default(null) })
    .refine((v) => v.target !== 'assignment' || v.id !== null, { message: 'An assignment link needs the assignment id', path: ['id'] });
/** The in-app route for a deep link (expo-router path). */
export function deepLinkPath(link) {
    if (!link)
        return '/notifications';
    if (link.target === 'assignment')
        return link.id ? `/assignment/${link.id}` : '/tasks';
    if (link.target === 'notifications')
        return '/notifications';
    return `/${link.target}`;
}
export const AUDIENCE_KINDS = ['USERS', 'TEAM', 'EVERYONE'];
export const audienceSchema = z.discriminatedUnion('kind', [
    /** One agent or a selection. */
    z.object({ kind: z.literal('USERS'), userIds: z.array(z.uuid()).min(1).max(1000) }),
    /** The members of one manager's team. */
    z.object({ kind: z.literal('TEAM'), managerId: z.uuid() }),
    /** Every active user who can use the field app. */
    z.object({ kind: z.literal('EVERYONE') }),
]);
const titleText = z.string().trim().min(1).max(80);
const bodyText = z.string().trim().min(1).max(500);
export const composeNotificationRequestSchema = z.object({
    audience: audienceSchema,
    titleAr: titleText,
    titleEn: titleText.nullable().default(null),
    bodyAr: bodyText,
    bodyEn: bodyText.nullable().default(null),
    deepLink: deepLinkSchema.nullable().default(null),
    /** Preview only: who would receive it, and how many phones; nothing is sent. */
    dryRun: z.boolean().default(false),
});
export const composePreviewSchema = z.object({
    recipients: z.number().int(),
    /** Recipients with at least one registered phone. */
    reachable: z.number().int(),
    devices: z.number().int(),
    /** Recipients who muted announcements: they get it in the in-app list only. */
    muted: z.number().int(),
    sample: z.array(z.object({ id: z.string(), fullName: z.string() })),
});
export const DELIVERY_STATUSES = ['QUEUED', 'SENT', 'DELIVERED', 'FAILED', 'NO_DEVICE', 'MUTED', 'NOT_SENT'];
const person = z.object({ id: z.string(), fullName: z.string() });
export const notificationSummarySchema = z.object({
    id: z.string(),
    kind: z.enum(NOTIFICATION_KINDS),
    titleAr: z.string(),
    titleEn: z.string().nullable(),
    bodyAr: z.string(),
    bodyEn: z.string().nullable(),
    deepLink: z.object({ target: z.enum(DEEP_LINK_TARGETS), id: z.string().nullable() }).nullable(),
    sender: person.nullable(),
    /** "Everyone", "Team of X", "3 agents"... as stored when sent. */
    audienceLabel: z.string(),
    recipientCount: z.number().int(),
    createdAt: z.string(),
    counts: z.record(z.enum(DELIVERY_STATUSES), z.number().int()),
});
export const deliveryRowSchema = z.object({
    id: z.string(),
    recipient: person,
    platform: z.string().nullable(),
    status: z.enum(DELIVERY_STATUSES),
    error: z.string().nullable(),
    sentAt: z.string().nullable(),
    checkedAt: z.string().nullable(),
    readAt: z.string().nullable(),
});
export const notificationDetailSchema = notificationSummarySchema.extend({ deliveries: z.array(deliveryRowSchema) });
export const notificationHistoryQuerySchema = paginationQuerySchema.extend({ kind: z.enum(NOTIFICATION_KINDS).optional() });
/** The agent's in-app list. */
export const inboxItemSchema = z.object({
    id: z.string(),
    notificationId: z.string(),
    kind: z.enum(NOTIFICATION_KINDS),
    title: z.string(),
    body: z.string(),
    titleEn: z.string().nullable(),
    bodyEn: z.string().nullable(),
    deepLink: z.object({ target: z.enum(DEEP_LINK_TARGETS), id: z.string().nullable() }).nullable(),
    createdAt: z.string(),
    readAt: z.string().nullable(),
});
export const inboxResponseSchema = z.object({
    items: z.array(inboxItemSchema),
    unread: z.number().int(),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
export const notificationPreferencesSchema = z.object({ muteNonEssential: z.boolean() });
/** The data payload on every push, read by the app when it arrives or is tapped. */
export const pushDataSchema = z.object({
    notificationId: z.string(),
    kind: z.enum(NOTIFICATION_KINDS),
    target: z.enum(DEEP_LINK_TARGETS).nullable(),
    id: z.string().nullable(),
});
/**
 * Android notification channels. Assignments and admin messages are high
 * importance (a heads-up banner, also when the app is closed); the shift
 * reminder is default. Android fixes a channel's importance when it is first
 * created, so raising announcements needed a new id (D104): `announcements`
 * (default importance, created by earlier builds) is deleted on start.
 */
export const ANDROID_CHANNELS = {
    assignments: { id: 'assignments', nameAr: 'المهام', nameEn: 'Assignments' },
    announcements: { id: 'announcements_high', nameAr: 'رسائل الإدارة', nameEn: 'Messages from the office' },
    shift: { id: 'shift', nameAr: 'الشيفت', nameEn: 'Shift' },
};
export const RETIRED_ANDROID_CHANNELS = ['announcements'];
/** Which channel a kind is shown on. A test uses the announcements channel, so it proves that path. */
export function channelFor(kind) {
    return kind === 'ASSIGNMENT' ? ANDROID_CHANNELS.assignments.id : ANDROID_CHANNELS.announcements.id;
}
// ---------------------------------------------------------------------------
// Push registration as the phone reports it (D104)
// ---------------------------------------------------------------------------
/**
 * Why a phone has (or has no) push token, reported by the app every time it
 * tries to register. Without this, "no registered phone" could mean anything.
 */
export const PUSH_REGISTRATION_STATUSES = [
    'REGISTERED',
    /** The agent has not been asked yet (the Arabic rationale comes first). */
    'PERMISSION_NOT_ASKED',
    'PERMISSION_DENIED',
    /** The build has no EAS project id, so Expo cannot issue a push token at all. */
    'NO_PROJECT_ID',
    /** An emulator: Expo issues no push token there. */
    'NOT_A_DEVICE',
    'ERROR',
];
export const reportPushStatusRequestSchema = z.object({
    status: z.enum(PUSH_REGISTRATION_STATUSES),
    detail: z.string().trim().max(500).nullable().optional(),
});
export function describePushRegistration(status, detail) {
    switch (status) {
        case 'REGISTERED':
            return { ar: 'الموبايل مسجّل للإشعارات.', en: 'The phone is registered for notifications.' };
        case 'PERMISSION_NOT_ASKED':
            return {
                ar: 'المندوب لسه ما وافقش على الإشعارات: التطبيق هيطلبها منه بشرح بالعربي.',
                en: 'The agent has not allowed notifications yet; the app will ask, with an explanation.',
            };
        case 'PERMISSION_DENIED':
            return {
                ar: 'الإشعارات مقفولة على الموبايل ده من إعدادات أندرويد. لازم المندوب يفتحها من الإعدادات.',
                en: 'Notifications are turned off for the app in the phone settings. The agent must turn them on.',
            };
        case 'NO_PROJECT_ID':
            return {
                ar: 'نسخة التطبيق دي اتبنت من غير رقم مشروع EAS، فـ Expo مش قادر يدّي الموبايل رقم إشعارات. لازم نسخة جديدة بعد ربط المشروع بـ EAS.',
                en: 'This app build has no EAS project id, so Expo cannot give the phone a push token. A new build is needed after linking the project to EAS.',
            };
        case 'NOT_A_DEVICE':
            return {
                ar: 'ده محاكي (emulator) مش موبايل حقيقي: الإشعارات بتشتغل على موبايل حقيقي بس.',
                en: 'This is an emulator; push notifications only work on a real phone.',
            };
        case 'ERROR':
            return {
                ar: `الموبايل ما قدرش يسجّل للإشعارات${detail ? `: ${detail}` : '.'}`,
                en: `The phone could not register for notifications${detail ? `: ${detail}` : '.'}`,
            };
        default:
            return {
                ar: 'الموبايل ده ما بلّغش عن حالة الإشعارات خالص: غالباً نسخة تطبيق قديمة. حدّث التطبيق.',
                en: 'This phone never reported its notification status, most likely an older app build. Update the app.',
            };
    }
}
/**
 * EAS_PROJECT_ID is missing (D108): said the same way by the app at start-up
 * and in its settings ('APP'), and by the panel's APK builder before a build
 * ('BUILDER'). Push cannot work without it, whatever else is configured.
 */
export function missingEasProjectId(where) {
    return where === 'APP'
        ? {
            ar: 'نسخة التطبيق دي من غير EAS_PROJECT_ID، فالإشعارات مش ممكن تشتغل عليها خالص. لازم نسخة جديدة متبنية بعد ضبط EAS_PROJECT_ID.',
            en: 'This app build has no EAS_PROJECT_ID, so push notifications cannot work on it. A new build made with EAS_PROJECT_ID set is needed.',
        }
        : {
            ar: 'EAS_PROJECT_ID مش موجود في apps/mobile/.env ولا في بيئة السيرفر: أي APK يتبني دلوقتي مش هيستقبل إشعارات خالص.',
            en: 'EAS_PROJECT_ID is not set in apps/mobile/.env or in the server environment: an APK built now can never receive push notifications.',
        };
}
/**
 * A delivery's outcome in plain words, from its status and the provider's
 * error (stored as "Code: message"). The codes are Expo's ticket and receipt
 * errors; anything else is shown with the provider's own message.
 */
export function describeDelivery(status, error) {
    const code = error?.split(':')[0]?.trim() ?? null;
    const message = error?.includes(':') ? error.slice(error.indexOf(':') + 1).trim() : error;
    switch (status) {
        case 'DELIVERED':
            return { ar: 'وصل لخدمة Google/Apple واتسلّم للموبايل.', en: 'Handed to Google/Apple for the phone.' };
        case 'SENT':
            return {
                ar: 'اتبعت لخدمة Expo، ومستنيين تأكيد التسليم (بياخد من ثواني لدقايق).',
                en: 'Sent to Expo; waiting for the delivery receipt (seconds to minutes).',
            };
        case 'QUEUED':
            return { ar: 'في الطابور، لسه ما اتبعتش.', en: 'Queued, not sent yet.' };
        case 'NO_DEVICE':
            return {
                ar: 'مفيش موبايل مسجّل للإشعارات للشخص ده، فاتحفظ في قائمة الإشعارات جوّه التطبيق بس.',
                en: 'No phone registered for notifications; it is only in the in-app list.',
            };
        case 'MUTED':
            return { ar: 'الشخص قافل إشعارات الإعلانات، فاتحفظ في القائمة جوّه التطبيق بس.', en: 'The person muted announcements.' };
        case 'NOT_SENT':
            if (code === 'PushDisabled') {
                return { ar: 'الإرسال مقفول على السيرفر (PUSH_PROVIDER=none).', en: 'Sending is disabled on the server (PUSH_PROVIDER=none).' };
            }
            return { ar: 'الموبايل عمل تسجيل خروج قبل الإرسال.', en: 'The phone signed out before it was sent.' };
        case 'FAILED':
            switch (code) {
                case 'InvalidCredentials':
                    return {
                        ar: 'مفتاح Firebase (FCM V1) مش مرفوع على حساب Expo، فخدمة Expo مش قادرة توصل لموبايلات أندرويد. ارفع المفتاح من expo.dev ← Credentials.',
                        en: 'The FCM V1 service-account key is not uploaded to Expo, so Expo cannot reach Android phones. Upload it under expo.dev → Credentials.',
                    };
                case 'DeviceNotRegistered':
                    return {
                        ar: 'رقم الإشعارات بتاع الموبايل ده مبقاش شغال (التطبيق اتمسح أو الإشعارات اتقفلت). اتشال، وهيتسجل تاني لما المندوب يفتح التطبيق.',
                        en: 'The phone’s push token is no longer valid (app removed or notifications disabled). It was removed and re-registers when the app opens.',
                    };
                case 'MismatchSenderId':
                    return {
                        ar: 'مشروع Firebase اللي في التطبيق غير المشروع اللي مفتاحه مرفوع على Expo.',
                        en: 'The Firebase project in the app is not the one whose key is uploaded to Expo.',
                    };
                case 'MessageTooBig':
                    return { ar: 'الرسالة أطول من المسموح.', en: 'The message is too large.' };
                case 'MessageRateExceeded':
                    return {
                        ar: 'اتبعت رسايل كتير للموبايل ده في وقت قصير. جرّب تاني بعد شوية.',
                        en: 'Too many messages to this phone; try again later.',
                    };
                case 'Unreachable':
                    return {
                        ar: 'السيرفر ما قدرش يوصل لخدمة Expo (الإنترنت أو الخدمة واقعة).',
                        en: 'The server could not reach the Expo push service.',
                    };
                default:
                    return {
                        ar: `خدمة الإشعارات رفضت الإرسال${message ? `: ${message}` : '.'}`,
                        en: `The push service refused it${message ? `: ${message}` : '.'}`,
                    };
            }
    }
}
/** "Send a test notification to myself": what happened on each of my phones (D104). */
export const TEST_OUTCOMES = ['DELIVERED', 'SENT', 'FAILED', 'NO_TOKEN'];
export const testNotificationResultSchema = z.object({
    notificationId: z.string(),
    devices: z.array(z.object({
        /** "Samsung SM-A515F (android)" or the platform alone. */
        label: z.string(),
        platform: z.string().nullable(),
        outcome: z.enum(TEST_OUTCOMES),
        reasonAr: z.string(),
        reasonEn: z.string(),
        deliveryId: z.string().nullable(),
    })),
    /** True while some phone is still SENT: ask again for the receipt. */
    pending: z.boolean(),
});
/** The text of an assignment notification, in both languages (D98). */
export function assignmentNotificationText(type, input) {
    const where = input.branchName ? ` (${input.branchName})` : '';
    const agentAr = input.agentName ?? 'المندوب';
    const agentEn = input.agentName ?? 'The agent';
    switch (type) {
        // The agent's own actions, addressed to their managers. Arabic copy pending native review.
        case 'ACCEPTED':
            return {
                titleAr: 'زيارة اتقبلت',
                titleEn: 'Visit accepted',
                bodyAr: `${agentAr} قبل زيارة يوم ${input.date}${where}.`,
                bodyEn: `${agentEn} accepted the visit for ${input.date}${where}.`,
            };
        case 'STARTED':
            return {
                titleAr: 'زيارة بدأت',
                titleEn: 'Visit started',
                bodyAr: `${agentAr} بدأ زيارة يوم ${input.date}${where}.`,
                bodyEn: `${agentEn} started the visit for ${input.date}${where}.`,
            };
        case 'COMPLETED':
            return {
                titleAr: 'زيارة خلصت',
                titleEn: 'Visit completed',
                bodyAr: `${agentAr} خلّص زيارة يوم ${input.date}${where}.`,
                bodyEn: `${agentEn} completed the visit for ${input.date}${where}.`,
            };
        case 'CREATED':
            return input.count === 1
                ? {
                    titleAr: 'زيارة جديدة مُسندة ليك',
                    titleEn: 'New assigned visit',
                    bodyAr: `اتسند لك زيارة يوم ${input.date}${where}.`,
                    bodyEn: `A visit was assigned to you for ${input.date}${where}.`,
                }
                : {
                    titleAr: 'زيارات جديدة مُسندة ليك',
                    titleEn: 'New assigned visits',
                    bodyAr: `اتسند لك ${input.count} زيارة يوم ${input.date}.`,
                    bodyEn: `${input.count} visits were assigned to you for ${input.date}.`,
                };
        case 'REASSIGNED_TO':
            return {
                titleAr: 'زيارة اتنقلت ليك',
                titleEn: 'A visit was moved to you',
                bodyAr: `اتنقلت لك زيارة يوم ${input.date}${where}.`,
                bodyEn: `A visit for ${input.date}${where} was moved to you.`,
            };
        case 'REASSIGNED_AWAY':
            return {
                titleAr: 'زيارة اتشالت من مهامك',
                titleEn: 'A visit was moved to another agent',
                bodyAr: `زيارة يوم ${input.date}${where} اتنقلت لمندوب تاني.`,
                bodyEn: `The visit for ${input.date}${where} was moved to another agent.`,
            };
        case 'RESCHEDULED':
            return {
                titleAr: 'ميعاد زيارة اتغير',
                titleEn: 'A visit was rescheduled',
                bodyAr: `زيارة${where} بقت يوم ${input.date}.`,
                bodyEn: `A visit${where} is now on ${input.date}.`,
            };
        case 'UPDATED':
            return {
                titleAr: 'تفاصيل زيارة اتغيرت',
                titleEn: 'A visit was updated',
                bodyAr: `المدير عدّل تفاصيل زيارة يوم ${input.date}${where}.`,
                bodyEn: `Your manager changed the details of the visit for ${input.date}${where}.`,
            };
        case 'CANCELLED':
            return {
                titleAr: 'زيارة اتلغت',
                titleEn: 'A visit was cancelled',
                bodyAr: `المدير لغى زيارة يوم ${input.date}${where}${input.reason ? `: ${input.reason}` : '.'}`,
                bodyEn: `Your manager cancelled the visit for ${input.date}${where}${input.reason ? `: ${input.reason}` : '.'}`,
            };
    }
}
//# sourceMappingURL=notifications.js.map